import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "dispatch911_token";

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

// ---- Auth ----
export const register = (payload) => api.post("/auth/register", payload).then((r) => r.data);
export const login = (payload) => api.post("/auth/login", payload).then((r) => r.data);
export const googleSignIn = (session_id) => api.post("/auth/google", { session_id }).then((r) => r.data);
export const me = () => api.get("/auth/me").then((r) => r.data);
export const changeName = (name) => api.post("/auth/change-name", { name }).then((r) => r.data);
export const forgotPassword = (email) =>
  api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, new_password) =>
  api.post("/auth/reset-password", { token, new_password }).then((r) => r.data);

// ---- Dev panel (developer role required) ----
export const devListUsers = ({ q = "", sort = "recent", limit = 50, offset = 0 } = {}) => {
  const p = new URLSearchParams();
  if (q) p.set("q", q);
  if (sort) p.set("sort", sort);
  p.set("limit", String(limit));
  p.set("offset", String(offset));
  return api.get(`/dev/users?${p.toString()}`).then((r) => r.data);
};
export const devUserDetail = (uid) => api.get(`/dev/users/${uid}`).then((r) => r.data);
export const devGrant = (uid, payload) =>
  api.post(`/dev/users/${uid}/grant`, payload).then((r) => r.data);
export const devTakeSnapshot = (uid) =>
  api.post(`/dev/users/${uid}/snapshot`).then((r) => r.data);
export const devRestoreSnapshot = (uid, sid) =>
  api.post(`/dev/users/${uid}/restore/${sid}`).then((r) => r.data);
export const devSetRole = (uid, role, grant) =>
  api.post(`/dev/users/${uid}/role`, { role, grant }).then((r) => r.data);
export const devDeleteUser = (uid) =>
  api.delete(`/dev/users/${uid}`).then((r) => r.data);
export const devCleanupEmptyAlliances = () =>
  api.post(`/dev/alliances/cleanup-empty`).then((r) => r.data);

// ---- Missions catalog ----
export const listMissions = () => api.get(`/missions`).then((r) => r.data);
export const devListMissions = () => api.get(`/dev/missions`).then((r) => r.data);
export const devCreateMission = (payload) =>
  api.post(`/dev/missions`, payload).then((r) => r.data);
export const devUpdateMission = (mid, payload) =>
  api.patch(`/dev/missions/${mid}`, payload).then((r) => r.data);
export const devDeleteMission = (mid) =>
  api.delete(`/dev/missions/${mid}`).then((r) => r.data);
export const devToggleMission = (mid) =>
  api.post(`/dev/missions/${mid}/toggle`).then((r) => r.data);

// ---- News / Announcements + Game Version ----
// Public (any signed-in player):
export const listNews = () => api.get(`/news`).then((r) => r.data);
export const getGameVersion = () => api.get(`/news/version`).then((r) => r.data);
export const getNewsUnread = () => api.get(`/news/unread-count`).then((r) => r.data);
export const markNewsSeen = () => api.post(`/news/mark-seen`).then((r) => r.data);
// Developer-only:
export const devCreateNews = (payload) =>
  api.post(`/news`, payload).then((r) => r.data);
export const devUpdateNews = (pid, payload) =>
  api.put(`/news/${pid}`, payload).then((r) => r.data);
export const devDeleteNews = (pid) =>
  api.delete(`/news/${pid}`).then((r) => r.data);
export const devSetGameVersion = (version) =>
  api.put(`/news/version`, { version }).then((r) => r.data);

// ---- Game ----
export const getState = () => api.get(`/game/state`).then((r) => r.data);
export const addStation = (payload) => api.post(`/game/stations`, payload).then((r) => r.data);
export const patchStation = (stationId, payload) =>
  api.patch(`/game/stations/${stationId}`, payload).then((r) => r.data);
export const buyBay = (stationId, payload = {}) =>
  api.post(`/game/stations/${stationId}/bay`, payload).then((r) => r.data);
export const finishBayNow = (stationId, pendingId) =>
  api.post(`/game/stations/${stationId}/bay/${pendingId}/finish`).then((r) => r.data);
export const quickFinishCall = (reward) =>
  api.post(`/game/calls/quick-finish`, { reward }).then((r) => r.data);
export const buyApparatus = (stationId, payload) =>
  api.post(`/game/stations/${stationId}/apparatus`, payload).then((r) => r.data);
export const sellApparatus = (stationId, apparatusId) =>
  api.delete(`/game/stations/${stationId}/apparatus/${apparatusId}`).then((r) => r.data);
export const buyStationSpecialtyBay = (stationId, type = "Landing Pad", payWith = "money") =>
  api.post(`/game/stations/${stationId}/specialty-bay`, { type, pay_with: payWith }).then((r) => r.data);

export const buyJailCell = (stationId, payWith = "money") =>
  api.post(`/game/stations/${stationId}/jail-cell`, { pay_with: payWith }).then((r) => r.data);

export const listEvents = () => api.get("/dev/events").then((r) => r.data);
export const createEvent = (payload) => api.post("/dev/events", payload).then((r) => r.data);
export const updateEvent = (id, payload) => api.patch(`/dev/events/${id}`, payload).then((r) => r.data);
export const deleteEvent = (id) => api.delete(`/dev/events/${id}`).then((r) => r.data);
export const listActiveEvents = () => api.get("/events/active").then((r) => r.data);

export const admitArrestee = (stationId, payload) =>
  api.post(`/game/stations/${stationId}/admit-arrestee`, payload).then((r) => r.data);

// ---- Hospitals ----
export const placeHospital = (p) => api.post(`/game/hospitals`, p).then((r) => r.data);
export const patchHospital = (hid, p) => api.patch(`/game/hospitals/${hid}`, p).then((r) => r.data);
export const sellHospital = (hid) => api.delete(`/game/hospitals/${hid}`).then((r) => r.data);
export const buyBed = (hid, payWith = "money") =>
  api.post(`/game/hospitals/${hid}/bed`, { pay_with: payWith }).then((r) => r.data);
export const buyHospitalBay = (hid, payWith = "money") =>
  api.post(`/game/hospitals/${hid}/bay`, { pay_with: payWith }).then((r) => r.data);
export const finishHospitalBayNow = (hid, pendingId) =>
  api.post(`/game/hospitals/${hid}/bay/${pendingId}/finish`).then((r) => r.data);
export const buyHospitalSpecialty = (hid, specialty, payWith = "money") =>
  api.post(`/game/hospitals/${hid}/specialty`, { specialty, pay_with: payWith }).then((r) => r.data);
export const buyHospitalApparatus = (hid, payload) =>
  api.post(`/game/hospitals/${hid}/apparatus`, payload).then((r) => r.data);
export const sellHospitalApparatus = (hid, aid) =>
  api.delete(`/game/hospitals/${hid}/apparatus/${aid}`).then((r) => r.data);
export const buyHospitalLandingPad = (hid, payWith = "money") =>
  api.post(`/game/hospitals/${hid}/landing-pad`, { pay_with: payWith }).then((r) => r.data);
export const listEligibleHospitals = () =>
  api.get(`/game/hospitals/eligible`).then((r) => r.data);
export const reserveBed = (hid, payload, ownerUserId, allianceId) =>
  api.post(`/game/hospitals/${hid}/reserve-bed`, payload, {
    params: {
      ...(ownerUserId ? { owner_user_id: ownerUserId } : {}),
      ...(allianceId ? { alliance_id: allianceId } : {}),
    },
  }).then((r) => r.data);
export const admitPatient = (hid, payload, ownerUserId, allianceId) =>
  api.post(`/game/hospitals/${hid}/admit`, payload, {
    params: {
      ...(ownerUserId ? { owner_user_id: ownerUserId } : {}),
      ...(allianceId ? { alliance_id: allianceId } : {}),
    },
  }).then((r) => r.data);
export const releaseBed = (hid, reservationId, ownerUserId, allianceId) =>
  api.post(`/game/hospitals/${hid}/release-bed`, { reservation_id: reservationId }, {
    params: {
      ...(ownerUserId ? { owner_user_id: ownerUserId } : {}),
      ...(allianceId ? { alliance_id: allianceId } : {}),
    },
  }).then((r) => r.data);
export const dischargePatient = (hid, patientId, allianceId) =>
  api.post(`/game/hospitals/${hid}/discharge`, { patient_id: patientId }, {
    params: allianceId ? { alliance_id: allianceId } : {},
  }).then((r) => r.data);
export const snapshotState = (payload) =>
  api.post(`/game/snapshot`, payload).then((r) => r.data);
export const resetGame = () => api.post(`/game/reset`).then((r) => r.data);

// ---- Mutual Aid ----
export const createAlliance = (p) => api.post(`/alliances`, p).then((r) => r.data);
export const listPublicAlliances = (params = {}) =>
  api.get(`/alliances`, { params }).then((r) => r.data);
export const allianceInfo = (aid) => api.get(`/alliances/${aid}/info`).then((r) => r.data);
export const allianceBuildings = (aid) =>
  api.get(`/alliances/${aid}/buildings`).then((r) => r.data);

// ---------- Friends ----------
export const listFriends = () => api.get("/friends").then((r) => r.data);
export const requestFriend = (query) =>
  api.post("/friends/request", { query }).then((r) => r.data);
export const acceptFriend = (fid) =>
  api.post(`/friends/${fid}/accept`).then((r) => r.data);
export const rejectFriend = (fid) =>
  api.post(`/friends/${fid}/reject`).then((r) => r.data);
export const removeFriend = (fid) =>
  api.delete(`/friends/${fid}`).then((r) => r.data);
export const listFriendMessages = (fid, since) =>
  api
    .get(`/friends/${fid}/messages`, { params: since ? { since } : {} })
    .then((r) => r.data);
export const sendFriendMessage = (fid, body) =>
  api.post(`/friends/${fid}/messages`, { body }).then((r) => r.data);
export const markFriendRead = (fid) =>
  api.post(`/friends/${fid}/read`).then((r) => r.data);
export const friendsNotifications = () =>
  api.get(`/friends/notifications`).then((r) => r.data);
export const myAlliance = () => api.get(`/alliances/mine`).then((r) => r.data);
export const joinAlliance = (aid) => api.post(`/alliances/${aid}/join`).then((r) => r.data);
export const approveMember = (aid, applicant_id) =>
  api.post(`/alliances/${aid}/approve`, null, { params: { applicant_id } }).then((r) => r.data);
export const rejectMember = (aid, applicant_id) =>
  api.post(`/alliances/${aid}/reject`, null, { params: { applicant_id } }).then((r) => r.data);
export const leaveAlliance = (aid) => api.post(`/alliances/${aid}/leave`).then((r) => r.data);
export const editAlliance = (aid, p) => api.patch(`/alliances/${aid}`, p).then((r) => r.data);
export const promoteMember = (aid, target_id) =>
  api.post(`/alliances/${aid}/promote`, { target_id }).then((r) => r.data);
export const demoteMember = (aid, target_id) =>
  api.post(`/alliances/${aid}/demote`, { target_id }).then((r) => r.data);
export const kickMember = (aid, target_id) =>
  api.post(`/alliances/${aid}/kick`, { target_id }).then((r) => r.data);
export const transferChief = (aid, target_id) =>
  api.post(`/alliances/${aid}/transfer-chief`, { target_id }).then((r) => r.data);
export const patchAllianceSettings = (aid, p) =>
  api.patch(`/alliances/${aid}/settings`, p).then((r) => r.data);
export const listAllianceHospitals = (aid) =>
  api.get(`/alliances/${aid}/hospitals`).then((r) => r.data);
export const placeAllianceHospital = (aid, payload) =>
  api.post(`/alliances/${aid}/hospitals`, payload).then((r) => r.data);
export const buyAllianceBed = (aid, hid) =>
  api.post(`/alliances/${aid}/hospitals/${hid}/bed`).then((r) => r.data);
export const buyAllianceSpecialty = (aid, hid, specialty) =>
  api.post(`/alliances/${aid}/hospitals/${hid}/specialty`, { specialty }).then((r) => r.data);
export const deleteAllianceHospital = (aid, hid) =>
  api.delete(`/alliances/${aid}/hospitals/${hid}`).then((r) => r.data);
export const renameAllianceHospital = (aid, hid, name) =>
  api.patch(`/alliances/${aid}/hospitals/${hid}`, { name }).then((r) => r.data);
export const setDonationPct = (pct) =>
  api.patch(`/game/donation`, { pct }).then((r) => r.data);
export const setHospitalFeePct = (pct) =>
  api.patch(`/game/hospital-fee`, { pct }).then((r) => r.data);
export const callResolved = (reward) =>
  api.post(`/game/call-resolved`, { reward }).then((r) => r.data);
export const allianceStations = (aid) => api.get(`/alliances/${aid}/stations`).then((r) => r.data);
export const allianceStationDetail = (aid, sid) =>
  api.get(`/alliances/${aid}/stations/${sid}`).then((r) => r.data);
export const shareCall = (aid, payload) => api.post(`/alliances/${aid}/share`, payload).then((r) => r.data);
export const listShared = (aid) => api.get(`/alliances/${aid}/shared`).then((r) => r.data);
export const helperDispatch = (aid, sid, payload) =>
  api.post(`/alliances/${aid}/shared/${sid}/dispatch`, payload).then((r) => r.data);
export const helperUpdate = (aid, sid, payload) =>
  api.post(`/alliances/${aid}/shared/${sid}/helper-update`, payload).then((r) => r.data);
export const upgradeShare = (aid, sid, payload) =>
  api.post(`/alliances/${aid}/shared/${sid}/upgrade`, payload).then((r) => r.data);
export const setRequesterUnits = (aid, sid, units, extra = {}) =>
  api
    .post(`/alliances/${aid}/shared/${sid}/requester-units`, { units, ...extra })
    .then((r) => r.data);
export const sharePatientTransport = (aid, sid, payload) =>
  api.post(`/alliances/${aid}/shared/${sid}/patient-transport`, payload).then((r) => r.data);
export const resolveShare = (aid, sid, payload) =>
  api.post(`/alliances/${aid}/shared/${sid}/resolve`, payload).then((r) => r.data);
export const closeShare = (aid, sid) => api.post(`/alliances/${aid}/shared/${sid}/close`).then((r) => r.data);
export const listAllianceMessages = (aid) => api.get(`/alliances/${aid}/messages`).then((r) => r.data);
export const postAllianceMessage = (aid, text) =>
  api.post(`/alliances/${aid}/messages`, { text }).then((r) => r.data);

// ---- Gems (real-money currency) ----
export const listGemPackages = () => api.get(`/gems/packages`).then((r) => r.data);
export const createGemCheckout = (package_id, origin_url) =>
  api.post(`/gems/checkout`, { package_id, origin_url }).then((r) => r.data);
export const getGemCheckoutStatus = (session_id) =>
  api.get(`/gems/checkout/status/${session_id}`).then((r) => r.data);
export const convertGems = (gems) =>
  api.post(`/gems/convert`, { gems }).then((r) => r.data);

// ---- Daily login bonus + achievements (retention loop) ----
export const getDailyStatus = () => api.get(`/daily/status`).then((r) => r.data);
export const claimDaily = () => api.post(`/daily/claim`).then((r) => r.data);
export const listAchievements = () => api.get(`/achievements`).then((r) => r.data);
export const recordGameEvent = (type, count = 1, xp = undefined) =>
  api.post(`/game/event`, xp != null ? { type, count, xp } : { type, count }).then((r) => r.data);
export const setMainStation = (station_id) =>
  api.post(`/game/main-station`, { station_id }).then((r) => r.data);
export const patchPreferences = (patch) =>
  api.patch(`/game/preferences`, patch).then((r) => r.data);

// ---- Profile / Premium ----
export const getProfile = () => api.get(`/profile`).then((r) => r.data);
export const getPremiumStatus = () => api.get(`/premium/status`).then((r) => r.data);
export const purchasePremium = () => api.post(`/premium/purchase`).then((r) => r.data);

// Map FastAPI 422 detail (array of objects) to a string for safe rendering.
export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
